import { useState } from "react";
import "../ComponentsStyling/Skills.css";
import { useGlobalReveal } from "../utils/useGlobalReveal";

/**
 * Skills — tabbed skills explorer with expandable capability lists.
 *
 * Selecting a category tab swaps the visible skill list; each skill row expands
 * to reveal its detailed capabilities. Tag styling is driven by TAG_COLOR.
 */

// Skill categories: each has tabs metadata plus a list of skills, where every
// skill carries a proficiency `tag` and an array of capability `details`.
const CATEGORIES = [
  {
    key: "frontend",
    label: "Frontend Developer",
    num: "01",
    sub: "Advanced",
    skills: [
      {
        name: "HTML", tag: "Professional",
        details: [
          "Semantic HTML5 structure", "Forms and input validation",
          "Accessibility best practices", "SEO optimization", "Modern HTML APIs",
        ],
      },
      {
        name: "CSS", tag: "Advanced",
        details: [
          "Flexbox & Grid layouts", "Responsive design techniques",
          "Animations & transitions", "CSS custom properties", "Modern CSS frameworks",
        ],
      },
      {
        name: "JavaScript", tag: "Intermediate",
        details: [
          "ES6+ syntax (arrow functions, destructuring, spread/rest)",
          "DOM manipulation & event handling",
          "Asynchronous JavaScript (Promises, Async/Await)",
          "Array methods (map, filter, reduce)",
          "Object-oriented programming", "Error handling & debugging",
          "API integration & fetch", "Local storage & session management",
          "Module systems (import/export)", "JavaScript design patterns",
        ],
      },
      {
        name: "React", tag: "Intermediate",
        details: [
          "React fundamentals (JSX, Components)",
          "Props & State management",
          "Hooks (useState, useEffect, useRef, useContext)",
          "Conditional rendering & component lifecycle",
          "Forms handling & validation",
          "React Router (v6, dynamic & nested routes)",
          "Context API & global state management",
          "Redux & Redux Toolkit",
          "Fetching & integrating REST APIs",
          "Authentication flows",
          "Performance optimization (memo, useCallback)",
          "CSS Modules & dynamic styling",
          "Project structure & best practices",
          "Deploying React applications",
        ],
      },
    ],
  },
  {
    key: "database",
    label: "Database",
    num: "02",
    sub: "Learning Phase",
    skills: [
      {
        name: "MySQL", tag: "Beginner",
        details: ["Database structure concepts", "Basic SQL queries", "Table relationships"],
      },
      {
        name: "MongoDB", tag: "Beginner",
        details: ["Document model concepts", "Basic CRUD operations", "Used in EcoNova project"],
      },
    ],
  },
];

/* Proficiency level -> dot colour. A single dot plus the word, not a filled
   pill with its own border: four bordered pills in one list put four small
   boxes of colour on screen and the level stops being readable at a glance.
   The header legend is generated from this object so the two cannot disagree —
   it previously listed three levels while four were in use. */
const LEVELS = {
  Professional: "#16a34a",
  Advanced:     "#2563eb",
  Intermediate: "#d97706",
  Beginner:     "#64748b",
};

export default function Skills() {
  const [sectionRef, inView] = useGlobalReveal({ threshold: 0.13, once: true });
  const [active,   setActive]   = useState("frontend");
  const [expanded, setExpanded] = useState(null);

  const cat = CATEGORIES.find(c => c.key === active);

  // Toggle a category tab; clicking the active tab collapses it. Resets any open skill.
  function pickTab(key) {
    setActive(p => p === key ? null : key);
    setExpanded(null);
  }

  // Toggle a skill's expanded capability list (single-open accordion).
  function pickSkill(name) {
    setExpanded(p => p === name ? null : name);
  }

  return (
    <section className="sk" id="skills" ref={sectionRef}>
      <div className="sk__dots"    aria-hidden="true" />
      <div className="sk__blob--a" aria-hidden="true" />
      <div className="sk__blob--b" aria-hidden="true" />

      {/* Header */}
      <div className={`sk__head${inView ? " sk__head--in" : ""}`}>
        <div className="sk__eyebrow">
          <span className="sk__ey-n">03</span>
          <span className="sk__ey-l" aria-hidden="true" />
          <span className="sk__ey-t">Skills</span>
        </div>

        <div className="sk__hl">
          <h2 className="sk__h2">
            <span className="sk__h2-a" style={{ "--d": "0s"   }}>My Skills</span>
            <span className="sk__h2-b" style={{ "--d": ".07s" }}>
              <em className="sk__em">&amp; Abilities</em>
            </span>
          </h2>

          <div className="sk__hl-right" style={{ "--d": ".13s" }}>
            <p className="sk__desc">
              From production-ready frontend to what I'm
              actively learning next. Select a category.
            </p>
            <div className="sk__legend">
              {Object.entries(LEVELS).map(([label, color]) => (
                <span key={label} className="sk__legend-item">
                  <span className="sk__legend-dot" style={{ background: color }} />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`sk__sep${inView ? " sk__sep--in" : ""}`} aria-hidden="true" />

      {/* Category tabs.
          No watermark numeral and no pulsing corner dot: the solid ink fill
          already says which tab is active, and a second and third indicator
          for one piece of state is noise. */}
      <div className={`sk__tabs${inView ? " sk__tabs--in" : ""}`}>
        {CATEGORIES.map((c, i) => {
          const on = active === c.key;
          return (
            <button
              key={c.key}
              className={`sk__tab${on ? " sk__tab--on" : ""}`}
              style={{ "--ti": i }}
              onClick={() => pickTab(c.key)}
              aria-expanded={on}
              aria-controls="sk-panel"
            >
              <span className="sk__tab-label">{c.label}</span>
              <span className="sk__tab-sub">{c.sub}</span>
            </button>
          );
        })}
      </div>

      {/* Empty-state hint when no category is selected */}
      {!active && (
        <div className="sk__hint">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M10 3v10M6 9l4 4 4-4"
              stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Select a category above to explore my skills
        </div>
      )}

      {/* Active category panel: skill rows with expandable detail grids */}
      {active && cat && (
        <div className="sk__panel" id="sk-panel" key={active}>
          {cat.skills.length === 0 ? (
            <div className="sk__empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <p>Coming soon — actively building in this area.</p>
            </div>
          ) : (
            <div className="sk__list">
              {cat.skills.map((s, i) => {
                const dot  = LEVELS[s.tag] || LEVELS.Beginner;
                const open = expanded === s.name;
                return (
                  <div
                    key={s.name}
                    className={`sk__item${open ? " sk__item--open" : ""}`}
                    style={{ "--si": i }}
                  >
                    <button
                      className="sk__row"
                      onClick={() => pickSkill(s.name)}
                      aria-expanded={open}
                    >
                      <span className="sk__row-name">{s.name}</span>
                      <span className="sk__row-level">
                        <span className="sk__row-dot" style={{ background: dot }} />
                        {s.tag}
                      </span>
                      <span className="sk__row-count">
                        {s.details.length} capabilities
                      </span>
                      <span className="sk__row-chev" aria-hidden="true">
                        <svg viewBox="0 0 10 6" fill="none">
                          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </span>
                    </button>

                    {/* No dark header strip inside the panel — the row above it
                        already names the skill and states the count. */}
                    <div className="sk__detail" aria-hidden={!open}>
                      {/* This wrapper carries min-height:0 + overflow:hidden and
                          must stay unpadded — a 0fr row collapses the content
                          box, but padding on the collapsing element still holds
                          the panel open and leaks the first row of the list. */}
                      <div className="sk__detail-inner">
                        <ul className="sk__caps">
                          {s.details.map((d, j) => (
                            <li key={j} className="sk__cap">
                              <svg className="sk__cap-mark" viewBox="0 0 12 12"
                                fill="none" aria-hidden="true">
                                <path d="M2.5 6.4l2.4 2.4 4.6-5.2" stroke="currentColor"
                                  strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              {d}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}